/**
 * A bar beside a number, for the cases where comparing beats reading: burn
 * against budget, share of a whole, billable percent, margin on a ranked list.
 *
 * The number stays. The bar is a second reading, never a replacement, which
 * is why `label` renders as text next to the track rather than inside it.
 *
 * `burn` is the only mode with thresholds. Past 85% the fill warns, past 100%
 * it is bad, and the track clamps at full while the label carries the true
 * figure ("131%"): a bar that kept growing would push the column apart, and
 * a bar that silently capped would hide the overrun.
 */
import { cn } from "@/lib/utils";

export type InlineBarMode = "burn" | "share" | "plain";
type Fill = "series" | "series2" | "warn" | "bad";
export type InlineBarSeries = "series1" | "series2" | "warn" | "bad";

export const BURN_WARN_AT = 0.85;
export const BURN_BAD_AT = 1.0;

function fillFor(value: number, mode: InlineBarMode, series: InlineBarSeries): Fill {
  const base: Fill = series === "series1" ? "series" : series;
  if (mode !== "burn") return base;
  if (value >= BURN_BAD_AT) return "bad";
  if (value >= BURN_WARN_AT) return "warn";
  return base;
}

const FILL_CLASS: Record<Fill, string> = {
  series: "bg-chart-series-1",
  series2: "bg-chart-series-2",
  warn: "bg-[var(--bar-fill-warn)]",
  bad: "bg-[var(--bar-fill-bad)]",
};

export function InlineBar({
  value,
  mode = "plain",
  width = 96,
  label,
  fill: series = "series1",
  className,
}: {
  /** A ratio: 0.34 is 34%, 1.31 is 131%. */
  value: number;
  mode?: InlineBarMode;
  /** Track width in px. Burn columns use 120, ranked lists 96. */
  width?: number;
  /** The figure, printed beside the track. */
  label?: string;
  /** Which colour the fill uses. `warn` and `bad` let a page apply its own thresholds. Burn thresholds still override. */
  fill?: InlineBarSeries;
  className?: string;
}) {
  const fill = fillFor(value, mode, series);
  const pct = Math.round(Math.min(1, Math.max(0, value)) * 100);
  return (
    <span className={cn("inline-flex items-center gap-2 whitespace-nowrap", className)}>
      <span
        data-slot="inline-bar"
        data-fill={fill}
        aria-hidden
        className="relative inline-block h-1.5 shrink-0 overflow-hidden rounded-full bg-muted"
        style={{ width }}
      >
        <i
          className={cn("absolute inset-y-0 left-0 rounded-full", FILL_CLASS[fill])}
          style={{ width: `${pct}%` }}
        />
      </span>
      {label !== undefined ? <span className="tabular-nums">{label}</span> : null}
    </span>
  );
}
